import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const GROOVE_JONES_INTRO_SRC = "/media/gj-brand-intro-stacked-1280-short.mp4";

// Gate the entire experience rather than covering an already running car intro.
// Its animation clock and header mount only when the logo actually finishes.
export default function GrooveJonesIntro({ children, src = GROOVE_JONES_INTRO_SRC }) {
  const [complete, setComplete] = useState(false);
  const [needsPlay, setNeedsPlay] = useState(false);
  const videoRef = useRef(null);
  const alive = useRef(false);
  const attempt = useRef(0);

  const finish = useCallback(() => {
    if (!alive.current) return;
    attempt.current += 1;
    setNeedsPlay(false);
    setComplete(true);
  }, []);

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video || !alive.current) return;
    const currentAttempt = ++attempt.current;
    setNeedsPlay(false);
    video.muted = true;
    const rejected = error => {
      if (!alive.current || currentAttempt !== attempt.current || error?.name === "AbortError") return;
      if (error?.name === "NotSupportedError") finish();
      else setNeedsPlay(true);
    };
    try {
      const pending = video.play();
      pending?.catch(rejected);
    } catch (error) {
      rejected(error);
    }
  }, [finish]);

  useLayoutEffect(() => {
    if (complete) return;
    const body = document.body;
    const html = document.documentElement;
    const previousBodyBackground = body.style.background;
    const previousHtmlBackground = html.style.background;
    const previousOverflow = body.style.overflow;
    body.style.background = "#000000";
    html.style.background = "#000000";
    body.style.overflow = "hidden";
    return () => {
      body.style.background = previousBodyBackground;
      html.style.background = previousHtmlBackground;
      body.style.overflow = previousOverflow;
    };
  }, [complete]);

  useEffect(() => {
    if (complete) return;
    alive.current = true;
    const video = videoRef.current;
    play();
    return () => {
      alive.current = false;
      attempt.current += 1;
      video?.pause();
    };
  }, [complete, play]);

  if (complete) return children;
  return (
    <section
      aria-label="Groove Jones introduction"
      style={{ position: "fixed", inset: 0, zIndex: 2147483647, background: "#000000",
        display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <video
        ref={videoRef}
        src={src}
        aria-label="Groove Jones logo animation"
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={finish}
        onError={finish}
        onPlay={() => setNeedsPlay(false)}
        style={{ width: "100%", height: "100%", display: "block", objectFit: "contain", background: "#000000" }}
      />
      {needsPlay && (
        <button
          type="button"
          onClick={play}
          style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
            border: "1px solid #ffffff", borderRadius: 4, padding: "10px 18px", color: "#ffffff",
            background: "#000000", font: "inherit", cursor: "pointer" }}
        >
          Play intro
        </button>
      )}
    </section>
  );
}
